import cx from 'clsx';
import { type ReactNode, useId } from 'react';
import {
  Button,
  Collection,
  Header,
  ListBox,
  ListBoxItem,
  ListBoxSection,
  Popover,
  Select,
  type SelectProps,
  SelectValue,
} from 'react-aria-components';
import { FormattedMessage } from 'react-intl';
import { IconChevronBottomOutlined } from '@repo/monochrome-icons';
import styles from './dropdown-selector.module.css';

interface DropdownItem {
  key: string;
  /**
   * @deprecated Do not pass translation keys via labelId,
   * pass the formatted message via label instead
   */
  labelId?: string;
  label?: string;
  optionLabel?: ReactNode;
  /**
   * @deprecated Do not pass translation keys via dropdownId
   */
  dropdownId?: string;
}

interface DropdownSection {
  key: string;
  dropdownId: string;
  items: DropdownItem[];
}

export type DropdownItems = DropdownItem[] | DropdownSection[];

interface DropdownSelectorProps extends Omit<SelectProps<DropdownItem>, 'onSelectionChange'> {
  items: DropdownItems;
  onSelectionChange: (key: string) => void;
  ariaLabel?: string;
  listBoxClassName?: string;
  listItemClassName?: string;
  popoverTriggerClassName?: string;
  buttonSize?: 'sm' | 'md';
  /**
   * Keeps the rest of the page interactive while the popover is open. Without it, react-aria
   * marks everything outside the popover `inert`, which — inside something like a side panel
   * with its own outside-click handling — makes a click on other content in the same panel
   * miss its target and read as a click outside the panel instead.
   */
  isNonModal?: boolean;
}

function hasGroupedItems(items: DropdownItems): items is DropdownSection[] {
  return items.every(item => 'items' in item);
}

export function DropdownSelector({
  items,
  onSelectionChange,
  className,
  listBoxClassName,
  listItemClassName,
  popoverTriggerClassName,
  buttonSize = 'md',
  isNonModal,
  ...props
}: DropdownSelectorProps): ReactNode {
  // react-aria modal escapes the test container, we render it where it can find the modal (i.e. <div> component with 'ember-testing' id)
  const TEST_PORTAL_CONTAINER = document.getElementById('ember-testing') ?? undefined;
  const triggerId = useId();

  return (
    <Select
      className={cx(styles.select, className)}
      data-testid="dropdown-selector"
      onSelectionChange={key => {
        onSelectionChange(key as string);
      }}
      {...props}
    >
      <Button
        className={cx(
          buttonSize === 'md' && 'body-prominent',
          buttonSize === 'sm' && 'body-standard',
          styles.popoverTrigger,
          popoverTriggerClassName
        )}
        data-testid="trigger"
        id={triggerId}
      >
        <SelectValue<DropdownItem>>
          {({ defaultChildren, selectedItem }) => {
            /** the whole render function can be removed once we stop passing labelId */
            if (selectedItem?.optionLabel) {
              return selectedItem.label;
            }
            if (selectedItem?.labelId) {
              return <FormattedMessage id={selectedItem.labelId} />;
            }
            return defaultChildren;
          }}
        </SelectValue>
        <IconChevronBottomOutlined
          aria-hidden="true"
          className={cx(
            styles.arrowIcon,
            buttonSize === 'md' && styles.arrowIconMd,
            buttonSize === 'sm' && styles.arrowIconSm
          )}
        />
      </Button>
      {/* aria-labelledby is set explicitly to avoid a race between RAC's context wiring and the axe accessibility sweep */}
      <Popover
        UNSTABLE_portalContainer={TEST_PORTAL_CONTAINER}
        aria-labelledby={triggerId}
        isNonModal={isNonModal}
      >
        {hasGroupedItems(items) ? (
          <ListBox
            aria-labelledby={triggerId}
            className={cx(styles.listBox, listBoxClassName)}
            data-testid="dropdown-options"
            items={items}
          >
            {(section: DropdownSection) => (
              <ListBoxSection data-testid={section.dropdownId} id={section.key} key={section.key}>
                <Header className={styles.sectionHeader}>
                  <FormattedMessage id={section.dropdownId} />
                </Header>
                <Collection items={section.items}>
                  {(item: DropdownItem) => (
                    <ListBoxItem
                      className={cx(styles.listItem, listItemClassName)}
                      data-testid={item.key}
                      key={item.key}
                    >
                      {item.optionLabel ?? item.label ?? <FormattedMessage id={item.dropdownId} />}
                    </ListBoxItem>
                  )}
                </Collection>
              </ListBoxSection>
            )}
          </ListBox>
        ) : (
          <ListBox
            aria-labelledby={triggerId}
            className={cx(styles.listBox, listBoxClassName)}
            data-testid="dropdown-options"
            items={items}
          >
            {item => (
              <ListBoxItem
                className={cx(styles.listItem, listItemClassName)}
                data-testid={item.key}
                key={item.key}
              >
                {item.optionLabel ?? item.label ?? <FormattedMessage id={item.dropdownId} />}
              </ListBoxItem>
            )}
          </ListBox>
        )}
      </Popover>
    </Select>
  );
}
